import type { Lesson, ProductModule } from '@/services/products.service';

/** Catalog filters are context only; the catalog still checks area membership. */
export function catalogContextSearch(search: string | URLSearchParams): string {
  const source = new URLSearchParams(search);
  const context = new URLSearchParams();
  for (const key of ['area', 'ecosystem', 'section']) {
    const value = source.get(key);
    if (value) context.set(key, value);
  }
  const query = context.toString();
  return query ? `?${query}` : '';
}

export function courseCatalogUrl(search: string | URLSearchParams): string {
  return `/courses${catalogContextSearch(search)}`;
}

export function courseLessonUrl(
  slug: string | undefined,
  lessonId: string,
  search: string | URLSearchParams,
): string {
  const path = slug
    ? `/courses/${encodeURIComponent(slug)}/lessons/${encodeURIComponent(lessonId)}`
    : `/lesson/${encodeURIComponent(lessonId)}`;
  return `${path}${catalogContextSearch(search)}`;
}

export function lessonReturnUrl(
  slug: string | undefined,
  search: string | URLSearchParams,
  directLesson = false,
): string {
  // Returning through a LESSON course would immediately reopen its player.
  if (directLesson || new URLSearchParams(search).has('section') || !slug) {
    return courseCatalogUrl(search);
  }
  return `/courses/${encodeURIComponent(slug)}${catalogContextSearch(search)}`;
}

export function orderedLessons(lessons: Lesson[] = []): Lesson[] {
  return lessons.filter(lesson => lesson.access?.allowed !== false).sort((a, b) => a.order - b.order);
}

/** Include direct lessons and descendants without mutating cached query data. */
export function orderedLessonModules(modules: ProductModule[] = []): ProductModule[] {
  const result: ProductModule[] = [];
  const seenModules = new Set<string>();
  const visit = (items: ProductModule[]) => {
    for (const module of [...items].sort((a, b) => a.order - b.order)) {
      if (module.access?.allowed === false) continue;
      if (seenModules.has(module.id)) continue;
      seenModules.add(module.id);
      if (module.lessons?.length) {
        result.push({ ...module, lessons: orderedLessons(module.lessons) });
      }
      visit(module.children || []);
    }
  };
  visit(modules);
  return result;
}

export function orderedCourseLessons(modules: ProductModule[] = []): Lesson[] {
  const seenLessons = new Set<string>();
  return orderedLessonModules(modules).flatMap((module) =>
    (module.lessons || []).filter((lesson) => {
      if (seenLessons.has(lesson.id)) return false;
      seenLessons.add(lesson.id);
      return true;
    }),
  );
}

export function courseEntryLesson(modules: ProductModule[] = []): Lesson | undefined {
  const lessons = orderedCourseLessons(modules);
  return lessons.find((lesson) => !lesson.completed) || lessons[0];
}
